import React from "react";
import { differenceInDays, format } from "date-fns";
import { useBookingsContext } from "../../Context/BookingsContext";
import { toZonedTime } from "date-fns-tz";
import { useTimeZoneContext } from "../../../../lib/context/TimeZone/TimeZoneContext";
import { useMediaQuery } from "../../../../lib/hooks/useMediaQuery";
import { getInitials } from "../../../../lib/helpers/getInitials";
import { trimText } from "../../../../lib/helpers/trimText";

import { MatchingDesk } from "../../../../api/grpc/desk/desk";
import {
  StructureTable,
  StructureTableColumn,
} from "../../../shared/StructureTable/StructureTable";
import { WorkplacePrefixIcon } from "../../../Workplaces/WorkplaceExpandedList/WorkplacePrefixIcon/WorkplacePrefixIcon";
import { MatchedWorkplaceActions } from "../../shared/MatchedWorkplaceActions";
import { trimArray } from "../../../../lib/helpers/trimArray";
import { Icon } from "../../../shared/Icon/Icon";
import { SearchDesk } from "../../../../api/grpc/workplaces/workplaces";
import { Tooltip } from "../../../shared/Tooltip/Tooltip";

interface Props {
  desks: MatchingDesk[];
  assignedDesk?: SearchDesk;
}

const colums: StructureTableColumn[] = [
  {
    key: "workplace_icon",
    header: "",
  },
  {
    key: "name",
    header: (
      <div className="flex-a-center">
        <Icon icon="location-search" />
        <span className="pl-1">Desk</span>
      </div>
    ),
  },
  {
    key: "nextReservation",
    header: (
      <div className="flex-a-center">
        <Icon icon="next-reservation" />
        <span className="pl-1">Next reservation</span>
      </div>
    ),
  },
  {
    key: "bookingsTags",
    header: (
      <div className="flex-a-center">
        <Icon icon="tags-search" />
        <span className="pl-1">Tags</span>
      </div>
    ),
  },
  {
    key: "organizer",
    header: "",
  },
  {
    key: "actions",
    header: "",
  },
  {
    key: "disableHover",
    header: "",
    hidden: true,
  },
];

export const BookingsDesksTable = ({ desks, assignedDesk }: Props) => {
  const { timeZone } = useTimeZoneContext();
  const { day } = useBookingsContext();
  const smallScreen = useMediaQuery("(max-width: 1200px)");

  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  const rows = desks.map((desk) => {
    return {
      workplace_icon: <WorkplacePrefixIcon icon="desk_icon_18x18" />,
      name: (
        <h4 className="Bookings__desk">
          <Tooltip content={desk.deskName}>
            {trimText(desk.deskName, smallScreen ? 150 : 22)}
          </Tooltip>
        </h4>
      ),
      nextReservation: !!desk.upcomingEvent.length ? (
        <span>
          {format(
            toZonedTime(new Date(desk.upcomingEvent), timeZone),
            differenceInDays(
              toZonedTime(new Date(day), timeZone),
              toZonedTime(new Date(desk.upcomingEvent), timeZone)
            ) === 0
              ? "HH:mm"
              : "dd MMM HH:mm"
          )}
        </span>
      ) : (
        <span className="pb-1 Bookings__empty">-</span>
      ),
      bookingsTags: (
        <>
          <span>{trimArray(desk.tagsFilter)}</span>
          {desk.tagsFilter.length > 1 && (
            <div>{desk.tagsFilter.join(", ")}</div>
          )}
        </>
      ),
      organizer:
        desk.event.length > 0 ? (
          <div className="Bookings__user flex-a-center">
            <span>{desk.event[0].organizer?.displayName}</span>
            {desk.event[0].organizer?.avatar?.url ? (
              <img src={desk.event[0]?.organizer?.avatar?.url} alt="User" />
            ) : (
              <span className="Bookings__user--initials flex-a-center">
                {getInitials(desk.event[0].organizer?.displayName || "")}
              </span>
            )}
          </div>
        ) : undefined,
      actions:
        desk.event.length > 0 ? undefined : (
          <MatchedWorkplaceActions
            workspaceId={desk.deskID}
            type="desk"
            locationPath={desk.locationPath}
            assignedDesk={assignedDesk}
            bookingWindow={desk.bookingWindow}
          />
        ),
      disableHover: desk.event.length > 0,
    };
  });

  return (
    <div>
      <StructureTable
        columns={colums}
        rows={rows}
        className="StructureTable--rooms StructureTable--desks"
        isMobile={isMobile}
      />
    </div>
  );
};
